/* ============================================================
   METAL FINGERS - ONE CART ROUTE

   Every way of adding something to the cart ends in the same place:
   the redesign Smart Cart opens. Cartridges from the selection
   drawer, OG Collection products, Merch, Sample Packs, the four-pack
   bundle, and the header cart icon.

   Landing on /cart is the one exception: that serves the theme's own
   cart page, sections/mf-cart-page.liquid. See the note further down.

   It also decides which layer is on top and says so, which is how the
   selection drawer knows to step underneath and when to come back:

     mf:cart:open    the Smart Cart is now the top layer
     mf:cart:close   the layer below is live again

   Nothing here touches checkout.
   ============================================================ */

(function () {
  'use strict';

  if (window.MFCart) return;

  var ROOT = ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/').replace(/\/$/, '');
  function isCartPath(path) {
    return /(^|\/)cart\/?$/.test(String(path || '').split('?')[0].split('#')[0]);
  }

  function smartCart() { return window.Rebuy && window.Rebuy.SmartCart; }

  function syncHeader(cart) {
    ['[data-header-cart-count]', '[data-cart-count]'].forEach(function (sel) {
      var nodes = document.querySelectorAll(sel);
      for (var i = 0; i < nodes.length; i++) nodes[i].textContent = cart.item_count;
    });
  }

  /* True when Rebuy's own copy of the cart already holds the same
     number of items at the same total as the cart just read. */
  function rebuyMatches(cart) {
    try {
      var own = window.Rebuy && window.Rebuy.Cart && window.Rebuy.Cart.cart;
      return !!(own && cart && own.item_count === cart.item_count && own.total_price === cart.total_price);
    } catch (e) { return false; }
  }

  function waitForMatch(cart, limit) {
    return new Promise(function (resolve) {
      if (rebuyMatches(cart)) { resolve(true); return; }
      var waited = 0;
      var timer = setInterval(function () {
        waited += 50;
        if (rebuyMatches(cart)) { clearInterval(timer); resolve(true); }
        else if (waited >= limit) { clearInterval(timer); resolve(false); }
      }, 50);
    });
  }

  var MFCart = {

    /* Read the real cart back, hand it to Rebuy, then open. One
       sequential path, so there is no race with Rebuy's own listeners
       and no chance of a second add. */
    open: function () {
      return fetch(ROOT + '/cart.js', { credentials: 'same-origin' })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          syncHeader(cart);
          var sc = smartCart();
          if (!sc) return;

          /* 20 Oct 2026: Rebuy refreshes its own copy of the cart after
             every add and every discount change. Handing it the cart a
             second time made it load every product again and doubled the
             wait, so it is only done if Rebuy has not caught up by itself
             within a few seconds. If it already matches, the cart opens
             straight away. */
          var ready = waitForMatch(cart, 3000).then(function (matched) {
            if (matched) return null;
            if (typeof sc.setCartAsync === 'function') return sc.setCartAsync(cart);
            if (typeof sc.setCart === 'function') sc.setCart(cart);
            return null;
          });

          return ready.then(function () {
            if (typeof sc.updateCartCount === 'function') sc.updateCartCount();
            if (typeof sc.updateCartSubtotal === 'function') sc.updateCartSubtotal();

            /* Resolve when the cart is actually on screen, not when it has
               merely been asked to appear.

               Callers use this promise to decide when the customer may act
               again - the selection drawer un-busies its Add button on it -
               so resolving early leaves that button live and pink while
               nothing has visibly changed. That reads as a failed click,
               and invites the second one that puts two of the same SKU in
               the bag. Waiting for the cart to be up closes that window.

               mf:cart:open is published by the observer further down, so
               this settles whatever opened the cart. The cap is a safety
               net and never the normal path: if Rebuy fails to open, the
               caller still has to be released rather than left waiting for
               ever. */
            return new Promise(function (resolve) {
              var settled = false;

              function finish() {
                if (settled) return;
                settled = true;
                endPending();
                document.removeEventListener('mf:cart:open', finish);
                clearTimeout(cap);
                resolve();
              }

              document.addEventListener('mf:cart:open', finish);
              var cap = setTimeout(finish, 2500);

              /* 20 Oct 2026 (Cart Update theme): opened straight away. It
                 used to wait a fixed 120ms first, which the customer felt
                 as lag on every add. */
              if (typeof sc.show === 'function') sc.show();
              /* Already open: no state change follows, so nothing would
                 publish mf:cart:open. Settle on the next tick instead. */
              if (cartIsOpen()) setTimeout(finish, 60);
            });
          });
        });
    },

    /* 20 Oct 2026: both add routes go through addThenShow (below), so
       the cart opens at once and is never filled twice. */
    add: function (variantId, quantity, properties) {
      var item = { id: variantId, quantity: quantity || 1 };
      if (properties) item.properties = properties;
      return addThenShow(function () {
        return fetch(ROOT + '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ items: [item] })
        });
      });
    },

    /* Adds every item in a normal Shopify add-to-cart form, so a
       multi-line bundle goes in as one request. */
    addForm: function (form) {
      var data = new FormData(form);
      return addThenShow(function () {
        return fetch(ROOT + '/cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
          body: data
        });
      });
    }
  };

  window.MFCart = MFCart;

  /* ---------- Fast add (20 Oct 2026) ----------

     Rebuy watches Shopify's /cart/add.js itself. The moment an add
     lands it re-reads the cart, loads the new product's details and
     opens the Smart Cart on its own. The old route ran a second copy
     of all of that straight after (read the cart again, hand it to
     Rebuy, open), and the two copies raced each other. That race is
     what made the cart open late, or open showing an empty bag with
     the new item missing until the next add.

     Now there is one route:

       1. The cart opens the moment Add is pressed, with a short
          "Adding to your bag" cover over it, so the customer sees
          something happen straight away and never sees a stale bag.
       2. Shopify adds the item.
       3. Rebuy refreshes the cart by itself. As soon as its count
          goes up, the cover lifts and the new item is there.
       4. Only if Rebuy has not caught up after a few seconds is the
          old manual refresh run, as a safety net. */

  var PENDING_CLASS = 'mf-cart-pending';
  var CATCH_UP_MS = 2500;

  function rebuyCount() {
    try {
      var c = window.Rebuy && window.Rebuy.Cart;
      if (c && typeof c.itemCount === 'function') return Number(c.itemCount()) || 0;
    } catch (e) {}
    return -1;
  }

  var pendingTimer = 0;

  function startPending() {
    var sc = smartCart();
    if (!sc || sc.status !== 'ready' || typeof sc.show !== 'function') return false;
    document.documentElement.classList.add(PENDING_CLASS);
    /* Never left up: whatever happens, the cover lifts after 6s. */
    clearTimeout(pendingTimer);
    pendingTimer = setTimeout(endPending, 6000);
    try { sc.show(); } catch (e) {}
    return true;
  }

  function endPending() {
    clearTimeout(pendingTimer);
    document.documentElement.classList.remove(PENDING_CLASS);
  }

  /* The Buy all 4 bundle runs its own add (it checks the bag first and
     attaches SAMPLE15), then calls MFCart.open() at the end. Open the
     cart with the cover the moment its button is pressed, so it feels
     as quick as every other add; MFCart.open() lifts the cover. */
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || !form.matches || !form.matches('#sample-packs .mf-sp__bundle-form')) return;
    var button = form.querySelector('[type="submit"]');
    if (button && button.disabled) return;
    startPending();
  }, true);

  /* Resolves once Rebuy's own copy of the cart holds more than it did
     before the add, or after CATCH_UP_MS with false. */
  function waitForRebuy(before) {
    return new Promise(function (resolve) {
      if (before < 0) { resolve(false); return; }
      var waited = 0;
      var timer = setInterval(function () {
        waited += 50;
        if (rebuyCount() > before) {
          clearInterval(timer);
          resolve(true);
        } else if (waited >= CATCH_UP_MS) {
          clearInterval(timer);
          resolve(false);
        }
      }, 50);
    });
  }

  function addThenShow(request) {
    var before = rebuyCount();
    var pending = startPending();

    return request()
      .then(function (res) {
        if (!res.ok) {
          return res.json().then(function (j) {
            throw new Error(j.description || j.message || 'add failed');
          });
        }
        return res.json();
      })
      .then(function (added) {
        return waitForRebuy(before).then(function (caughtUp) {
          if (caughtUp) {
            /* Rebuy has the item. Make sure the cart is up (it normally
               already is) and lift the cover. */
            var sc = smartCart();
            if (sc && typeof sc.show === 'function' && !cartIsOpen()) sc.show();
            if (sc && typeof sc.updateCartCount === 'function') sc.updateCartCount();
            syncHeader({ item_count: rebuyCount() });
            endPending();
            return added;
          }
          /* No Rebuy at all (an ad blocker or VPN can stop it loading):
             the item is in, so show it on the full cart page instead of
             leaving the customer with no sign that anything happened. */
          if (!smartCart()) {
            endPending();
            window.location.href = ROOT + '/cart';
            return added;
          }
          /* Safety net: the old manual refresh. */
          return MFCart.open().then(function () {
            endPending();
            return added;
          });
        });
      })
      .catch(function (err) {
        endPending();
        if (pending) {
          /* Leave the cart showing what is really in it. */
          MFCart.open();
        }
        throw err;
      });
  }


  /* ---------- Which layer is on top ----------

     Rebuy marks its own open state on the document: body gains
     rebuy-cart-visible and the cart wrapper gains is-visible. Watching
     that covers every way the cart can be opened or closed, including
     Rebuy's own close button and its backdrop, which a wrapped show()
     would miss.

     This is written as a reconciliation rather than as edge detection.
     Edge detection assumes it has seen every transition, and a single
     missed one - a class that flickered during a Rebuy re-render, a
     drawer built after the cart had already opened - would leave the
     two layers permanently disagreeing, with a live, focusable drawer
     sitting under the cart. Instead the truth is re-checked on every
     relevant mutation: if the cart is up and the drawer is not marked
     as being underneath it, say so again. The drawer's handler is
     idempotent, so a repeat costs nothing and a miss cannot persist. */

  var cartWasOpen = false;

  function cartIsOpen() {
    if (document.body.classList.contains('rebuy-cart-visible')) return true;
    var wrap = document.querySelector('.rebuy-cart');
    return !!(wrap && wrap.classList.contains('is-visible'));
  }

  function say(name) { document.dispatchEvent(new CustomEvent(name)); }

  function publishCartState() {
    watchCartContent();
    attachStateObservers();
    scheduleLinks();
    var open = cartIsOpen();
    var drawer = document.getElementById('MFNeedleDrawer');
    var drawerOpen = !!(drawer && drawer.hasAttribute('data-open'));
    var drawerUnder = !!(drawer && drawer.hasAttribute('data-under'));

    if (open !== cartWasOpen) {
      cartWasOpen = open;
      say(open ? 'mf:cart:open' : 'mf:cart:close');
    }

    /* Whatever the history, these two must agree. */
    if (open && drawerOpen && !drawerUnder) say('mf:cart:open');
    else if (!open && drawerUnder) say('mf:cart:close');
  }

  /* ---------- Watching the minimum ----------

     Three nodes decide the answer and only three: body carries
     rebuy-cart-visible, the cart wrapper carries is-visible, and the
     drawer carries data-open and data-under.

     This used to be a single document-wide observer watching class on
     every element in the page, subtree included. Rebuy re-renders its
     cart by touching classes throughout its own tree, so a single
     re-render woke this reconciliation hundreds of times, and each of
     those calls dragged a full link sweep along behind it. None of
     those mutations could ever have changed the answer.

     Watching the three nodes directly, with no subtree, keeps the
     reconciliation documented above exactly as it was - it still runs
     on every mutation that could change which layer is on top, so a
     missed transition still cannot leave the two layers disagreeing.
     What is gone is being woken by mutations that never mattered.

     body is there from the start. The wrapper is built by Rebuy later
     and can be replaced outright, so both are re-checked on every pass
     and re-attached whenever the node underneath has changed. */

  var cartStateObserver = null, cartStateNode = null;
  var drawerObserver = null, drawerNode = null;

  function attachStateObservers() {
    if (!window.MutationObserver) return;

    var wrap = document.querySelector('.rebuy-cart');
    if (wrap && wrap !== cartStateNode) {
      if (cartStateObserver) cartStateObserver.disconnect();
      cartStateNode = wrap;
      cartStateObserver = new MutationObserver(publishCartState);
      cartStateObserver.observe(wrap, { attributes: true, attributeFilter: ['class'] });
    }

    var drawer = document.getElementById('MFNeedleDrawer');
    if (drawer && drawer !== drawerNode) {
      if (drawerObserver) drawerObserver.disconnect();
      drawerNode = drawer;
      drawerObserver = new MutationObserver(publishCartState);
      drawerObserver.observe(drawer, {
        attributes: true,
        attributeFilter: ['data-open', 'data-under']
      });
    }
  }

  function watchCart() {
    if (!window.MutationObserver || !document.body) return;

    /* body's own class, and body's own children - deliberately NOT the
       subtree. The class is what Rebuy toggles to say the cart is up;
       the child list is how a newly built cart wrapper announces
       itself, so the targeted observers above can move onto it. */
    new MutationObserver(publishCartState).observe(document.body, {
      attributes: true,
      attributeFilter: ['class'],
      childList: true
    });

    attachStateObservers();
    publishCartState();
  }

  /* ---------- Empty cart: SHOP NOW ----------

     Where the empty cart sends somebody is decided HERE, not in Rebuy.

     It was set in the Smart Cart's own link editor, which meant two
     things went wrong. It wrote target="_blank" onto the button, and a
     new tab is a fresh request carrying none of the preview
     parameters. And the destination itself is only as good as whichever
     Smart Cart happens to be serving the page: without
     preview_smart_cart in the address the storefront falls back to the
     production cart, whose button still points at the old
     /collections/all products page. Same theme, same header, old
     destination - which is exactly what it looks like when the redesign
     is "sending you to the old page".

     So the theme sets it. Whatever cart is loaded and whatever anyone
     typed into Rebuy's link editor, the empty-cart button goes to the
     redesign Shop All page, in the same tab, carrying any preview
     parameters that are in the address. Nothing in Rebuy has to be
     right for this to be right.

     Scoped to the empty-state button alone. Everything else in the cart
     keeps its own destination and only loses target="_blank". Rebuy's
     outbound credit link is left alone: it is not ours, and a new tab
     is right for it. */

  var SHOP_ALL = ROOT + '/pages/shop-all';

  var PREVIEW_KEYS = ['preview_theme_id', 'preview_smart_cart', 'cart_version', '_ab', '_fd', '_sc'];

  function previewQuery() {
    try {
      var here = new URLSearchParams(location.search);
      var carry = [];
      for (var i = 0; i < PREVIEW_KEYS.length; i++) {
        var k = PREVIEW_KEYS[i];
        if (here.has(k)) carry.push(encodeURIComponent(k) + '=' + encodeURIComponent(here.get(k)));
      }
      return carry.join('&');
    } catch (e) { return ''; }
  }

  /* The empty state's button, whichever cart drew it. Matched on Rebuy's
     own empty-cart container so it cannot catch anything else. */
  function emptyStateLink() {
    var box = document.querySelector('.rebuy-cart__flyout-empty-cart');
    if (!box) return null;
    var a = box.querySelector('a[href]');
    if (!a) return null;
    var href = a.getAttribute('href') || '';
    /* Same-origin only. An absolute link in there is not ours to move. */
    if (href.charAt(0) !== '/') {
      try { if (new URL(href, location.href).origin !== location.origin) return null; }
      catch (e) { return null; }
    }
    return a;
  }

  function keepInTab() {
    /* 20 Oct 2026 (Cart Update theme): the empty-cart SHOP NOW button is
       no longer forced to /pages/shop-all. Its destination is now set in
       Rebuy's own Smart Cart editor, so it can be changed there. */

    /* Scoped to the cart wrapper when there is one. The links that need
       correcting are all inside it, and it is already in hand. */
    var scope = cartStateNode || document;
    var links = scope.querySelectorAll('.rebuy-cart__flyout a[href]');
    var carry = previewQuery();
    var stamp = carry || '-';

    for (var i = 0; i < links.length; i++) {
      var a = links[i];

      /* Already dealt with, and nothing about the address has changed
         since. Held as a property rather than an attribute so that
         marking a link cannot itself produce a mutation record and wake
         the observer that called us. Rebuy's re-renders build new
         nodes, which arrive without the mark and are corrected
         normally. */
      if (a.__mfTab === stamp) continue;
      a.__mfTab = stamp;

      var href = a.getAttribute('href') || '';

      /* Ours are the site's own URLs, whether they were written as a path
         or in full. Rebuy writes some of each, and an absolute link back
         to our own domain still needs the preview parameters and still
         must not open in a new tab. Anything on another origin belongs to
         somebody else and is left alone. */
      if (href.charAt(0) !== '/') {
        var absolute;
        try { absolute = new URL(href, location.href); } catch (e) { continue; }
        if (absolute.origin !== location.origin) continue;
        href = absolute.pathname + absolute.search + absolute.hash;
      }

      if (a.getAttribute('target')) {
        a.removeAttribute('target');
        a.removeAttribute('rel');
      }

      if (!carry || a.getAttribute('data-mf-preview') === carry) continue;

      var hash = href.indexOf('#') === -1 ? '' : href.slice(href.indexOf('#'));
      var bare = hash ? href.slice(0, href.indexOf('#')) : href;
      var path = bare.split('?')[0];
      var existing = bare.split('?')[1] || '';

      /* The page's own parameters win; the preview keys are only added
         where the link did not already say something about them. */
      var merged = existing ? existing + '&' + carry : carry;
      try {
        var seen = new URLSearchParams();
        new URLSearchParams(merged).forEach(function (v, k) { if (!seen.has(k)) seen.set(k, v); });
        merged = seen.toString();
      } catch (e) {}

      a.setAttribute('href', path + '?' + merged + hash);
      a.setAttribute('data-mf-preview', carry);
    }
  }

  /* One sweep per frame, at most.

     Rebuy emits many mutation records for a single re-render, and every
     one of them used to run a complete link sweep: the cost was
     mutations multiplied by links, which is why it grew so sharply with
     a fuller cart. Coalescing onto a frame makes one re-render cost one
     pass, and the pass itself now skips links it has already corrected.

     A frame rather than a timeout because the sweep has to have
     finished before the customer can see - and click - the links it
     corrects, and a frame callback lands before the next paint. */

  var linkFrame = 0;

  function scheduleLinks() {
    if (linkFrame) return;
    var raf = window.requestAnimationFrame || function (fn) { return setTimeout(fn, 16); };
    linkFrame = raf(function () {
      linkFrame = 0;
      try { keepInTab(); } catch (e) {}
    });
  }

  var cartContentObserver = null, cartContentNode = null;

  /* Re-attached if Rebuy replaces the wrapper outright, which the old
     one-shot guard could not survive: it would go on watching a
     detached node and the links in the new cart would never be seen. */
  function watchCartContent() {
    if (!window.MutationObserver) return;
    var wrap = document.querySelector('.rebuy-cart');
    if (!wrap || wrap === cartContentNode) return;
    if (cartContentObserver) cartContentObserver.disconnect();
    cartContentNode = wrap;
    cartContentObserver = new MutationObserver(scheduleLinks);
    cartContentObserver.observe(wrap, { childList: true, subtree: true });
    keepInTab();
  }

  /* ---------- One cart, whichever way you get there ----------

     Any normal Shopify add-to-cart form on the page is intercepted in
     the capture phase, added over AJAX, and finished in the Smart
     Cart. This covers OG Collection quick views, individual Sample
     Packs and the Sample Pack bundle without editing each section.
     With JavaScript off the form still posts normally, so nothing is
     lost.                                                            */

  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || form.tagName !== 'FORM') return;
    var action = form.getAttribute('action') || '';
    if (action.indexOf('/cart/add') === -1) return;
    if (form.hasAttribute('data-mf-ignore')) return;

    var button = form.querySelector('[type="submit"]');

    /* A form the theme has deliberately disabled is not purchasable -
       the Sample Pack bundle while one of its four packs is out of
       stock, for instance. It must not be talked into adding whatever
       lines happen to be left. */
    if (button && button.disabled) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    if (button) button.setAttribute('aria-busy', 'true');

    MFCart.addForm(form)
      .catch(function (err) { if (window.console) console.warn('[MF cart]', err); })
      .then(function () { if (button) button.removeAttribute('aria-busy'); });
  }, true);

  /* The header cart control and any View Cart link open the Smart
     Cart instead of leaving the page. Modified clicks are left alone,
     so cmd-click and open-in-new-tab still behave normally - and if
     they do land on /cart, the theme's own cart page is waiting.

     Links marked data-mf-ignore are skipped, which is how the cart
     page's own quantity and remove links reach Shopify's cart routes
     instead of being swallowed here. */
  document.addEventListener('click', function (e) {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    var link = e.target.closest && e.target.closest('a[href]');
    if (!link) return;
    if (link.hasAttribute('data-mf-ignore')) return;
    var href = link.getAttribute('href') || '';
    if (!isCartPath(href)) return;

    /* 20 Oct 2026 (Cart Update theme): the bag icon always opens the
       drawer. If it is tapped before Rebuy has finished loading (common
       on a phone, straight after the page opens), wait for it instead
       of falling through to the cart page. Only if Rebuy never loads
       does the link go to /cart as normal. */
    e.preventDefault();
    if (smartCart()) { MFCart.open(); return; }

    var waited = 0;
    var timer = setInterval(function () {
      waited += 100;
      if (smartCart()) {
        clearInterval(timer);
        MFCart.open();
      } else if (waited >= 6000) {
        clearInterval(timer);
        window.location.href = link.href;
      }
    }, 100);
  }, true);

  /* ---------- The preview travels with the customer ----------

     A theme preview lasts only as long as its parameters stay in the
     address. Most links on the site are written by Liquid and carry
     nothing, and the selection drawer builds its product links from
     scratch, so one click can drop somebody onto the published theme
     with no visible sign that it happened - the page still looks like
     the shop, it is simply the wrong version of it.

     Rather than teach every section to carry the parameters, they are
     added here, once, at the moment of the click, to same-origin links
     only. On a real visit there are no preview parameters in the
     address, so this does nothing at all and costs nothing after the
     theme is published. */

  document.addEventListener('click', function (e) {
    if (e.defaultPrevented) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;

    var carry = previewQuery();
    if (!carry) return;

    var link = e.target.closest && e.target.closest('a[href]');
    if (!link) return;
    if (link.hasAttribute('download') || link.getAttribute('target')) return;

    var raw = link.getAttribute('href') || '';
    /* An in-page anchor must stay an in-page anchor. */
    if (!raw || raw.charAt(0) === '#') return;

    var url;
    try { url = new URL(raw, location.href); } catch (err) { return; }
    if (url.origin !== location.origin) return;

    /* The link's own parameters win; the preview keys only fill gaps. */
    var params = url.searchParams;
    new URLSearchParams(carry).forEach(function (value, key) {
      if (!params.has(key)) params.set(key, value);
    });

    var query = params.toString();
    link.setAttribute('href', url.pathname + (query ? '?' + query : '') + url.hash);
  }, true);

  /* ---------- /cart ----------

     /cart used to bounce the customer back where they came from with
     the drawer open, because the cart page was still the old theme's
     and looked nothing like the shop. It now has its own on-theme page
     (sections/mf-cart-page.liquid), so there is nothing to hide from
     and the bounce has been removed rather than patched around.

     This matters most for the links the theme does not own: the bag
     icon inside Shopify's checkout, "view cart" links in email, and
     the Shop app. Those are full page loads from outside the site, so
     no amount of in-page JavaScript could ever turn them into the
     drawer - they were simply bouncing straight back out again.

     In-site cart links are unaffected: the click handler above still
     opens the drawer without leaving the page. */

  function start() {
    watchCart();
    watchCartContent();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
